/**
 * OtpLoginPage — "Email me a code" login flow, shared by brand + admin.
 *
 * Firebase-agnostic like AuthLoginPage: the parent wrapper owns the actual
 * BE calls (onRequestOtp/onVerifyOtp) and the post-verify Firebase sign-in.
 * Once onVerifyOtp resolves, the wrapper's own `user`-driven redirect effect
 * (the same one every login page already has — see each app's LoginPage.tsx)
 * takes the user away from this page; there is no navigation here.
 *
 * Email and code entry live in one continuous panel (matching AuthLoginPage's
 * static email+password layout) rather than swapping to a different page
 * state — once a code is sent, the email row becomes read-only with a pencil
 * icon to go back and edit it.
 *
 * Two entry points:
 *  - Normal: email → code (this component collects both).
 *  - From the emailed link (?email=&link_token=): auto-verifies once on
 *    mount, no user input required beyond opening the link.
 */
import React, { useEffect, useRef, useState } from 'react';
import { CircleNotch, PencilSimple, WarningCircle } from '../../design-system/icons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { AuthSplitLayout } from './AuthSplitLayout';
import { useOtpFlow } from './useOtpFlow';

type LinkState = 'idle' | 'verifying-link' | 'link-error';

export interface OtpLoginPageProps {
  appName?: string;
  /** Always resolves (enumeration-safe) — the backend never reveals eligibility. */
  onRequestOtp: (email: string) => Promise<void>;
  /** Throws on failure (invalid/expired/max-attempts); resolves once signed in. */
  onVerifyOtp: (params: { email: string; code?: string; linkToken?: string }) => Promise<void>;
  /** Present when arriving via the emailed link — triggers the auto-verify path. */
  linkToken?: string;
  /** Carried alongside linkToken from the emailed link's query params. */
  initialEmail?: string;
  /** Right-hand visual panel, shown across every step (matches AuthLoginPage's `sidePanel`). Omit for the centered layout. */
  sidePanel?: React.ReactNode;
  /** Search params the "Back to login" link keeps (e.g. a post-login `redirect`). */
  backSearch?: Record<string, string | undefined>;
  /** Overrides the panel headline, shown for both the email and code steps (default "Sign in with a code."). */
  headline?: string;
  /** Overrides the panel subheadline, shown for both steps. */
  subheadline?: string;
  /** Overrides the code-step submit button's label (default `Sign in to ${appName}`). */
  submitLabel?: string;
  /**
   * Overrides the inline text shown above the code field once a code has
   * been sent (default assumes the request also emails a sign-in link —
   * pass this when a caller's OTP is code-only, e.g. the domain-join
   * request flow).
   */
  codeSubheadline?: string;
  /** Extra line shown on its own below the code-step subheadline. Omit for none. */
  codeHint?: string;
}

export function OtpLoginPage({
  appName = 'Discovr',
  onRequestOtp,
  onVerifyOtp,
  linkToken,
  initialEmail,
  sidePanel,
  backSearch,
  headline,
  subheadline,
  submitLabel,
  codeSubheadline,
  codeHint,
}: OtpLoginPageProps) {
  const [linkState, setLinkState] = useState<LinkState>(linkToken ? 'verifying-link' : 'idle');
  const [linkError, setLinkError] = useState<string | null>(null);
  const otp = useOtpFlow({
    initialEmail,
    onRequestOtp,
    onVerifyOtp: ({ email, code }) => onVerifyOtp({ email, code }),
  });

  const ranLinkVerify = useRef(false);
  useEffect(() => {
    if (!linkToken || ranLinkVerify.current) return;
    ranLinkVerify.current = true;
    onVerifyOtp({ email: initialEmail ?? '', linkToken }).catch((e: unknown) => {
      setLinkError(e instanceof Error ? e.message : 'This link is no longer valid.');
      setLinkState('link-error');
    });
  }, [linkToken, initialEmail, onVerifyOtp]);

  const { isCodeStep, email, setEmail, code, setCode, requesting, verifying, error, isCoolingDown, countdown } = otp;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void (isCodeStep ? otp.verifyCode() : otp.requestCode());
  };

  if (linkState === 'verifying-link') {
    return (
      <AuthSplitLayout backTo="/login" backSearch={backSearch} headline="Signing you in…" sidePanel={sidePanel}>
        <div className="flex justify-center py-8">
          <CircleNotch size={32} className="animate-spin text-muted-foreground" />
        </div>
      </AuthSplitLayout>
    );
  }

  if (linkState === 'link-error') {
    return (
      <AuthSplitLayout
        backTo="/login" backSearch={backSearch}
        headline="This link is no longer valid."
        subheadline={linkError ?? 'Request a new code to sign in.'}
        sidePanel={sidePanel}
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => setLinkState('idle')}
          className="h-11 w-full rounded-full"
        >
          Request a new code
        </Button>
      </AuthSplitLayout>
    );
  }

  return (
    <AuthSplitLayout
      backTo="/login" backSearch={backSearch}
      headline={headline ?? 'Sign in with a code.'}
      subheadline={
        isCodeStep ? undefined : (subheadline ?? "Enter your work email and we'll send you a 6-digit code and a sign-in link.")
      }
      helper={isCodeStep ? "Can't find it? Check spam, or resend the code. It expires in 10 minutes." : undefined}
      sidePanel={sidePanel}
    >
      <form onSubmit={(e) => { void handleSubmit(e); }} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          {isCodeStep ? (
            <>
              <span className="text-body-sm-medium text-foreground">Work email</span>
              <div className="flex h-11 items-center justify-between rounded-lg border border-border bg-muted px-3 text-body-md-regular text-foreground">
                <span className="truncate">{email}</span>
                {/* Local provider: this page renders outside the authenticated
                    app shell (and whatever TooltipProvider it sets up), so the
                    tooltip needs its own. */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={otp.editEmail}
                        aria-label="Edit email"
                        className="size-7 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
                      >
                        <PencilSimple size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit email</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </>
          ) : (
            <>
              <label htmlFor="otp-email" className="text-body-sm-medium text-foreground">
                Work email
              </label>
              <Input
                id="otp-email"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={requesting}
                required
                autoFocus
                className="h-11 rounded-lg border-border bg-muted focus:bg-card"
              />
            </>
          )}
        </div>

        {isCodeStep && (
          <div className="flex flex-col gap-1">
            <label htmlFor="otp-code" className="text-body-sm-medium text-foreground">
              6-digit code
            </label>
            <p className="text-caption-regular text-muted-foreground">
              {codeSubheadline ?? 'We sent a 6-digit code and a sign-in link to the email above.'}
            </p>
            {codeHint && <p data-testid="otp-code-hint" className="text-caption-regular text-muted-foreground">{codeHint}</p>}
            <Input
              id="otp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              disabled={verifying}
              required
              autoFocus
              className="h-11 rounded-lg border-border bg-muted text-center text-body-md-medium tracking-[0.3em] focus:bg-card"
            />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2.5 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-700">
            <WarningCircle size={15} className="mt-px shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          disabled={isCodeStep ? verifying || code.trim().length !== 6 : requesting || !email.trim()}
          className="h-11 w-full rounded-full bg-primary text-body-sm-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {(isCodeStep ? verifying : requesting) ? <CircleNotch size={16} className="mr-2 animate-spin" /> : null}
          {isCodeStep
            ? verifying
              ? 'Signing in…'
              : (submitLabel ?? `Sign in to ${appName}`)
            : requesting
              ? 'Sending…'
              : 'Send code'}
        </Button>

        {isCodeStep && (
          <button
            type="button"
            onClick={() => { void otp.resend(); }}
            disabled={isCoolingDown || requesting}
            className="text-center text-body-sm-medium text-[#6c09f7] hover:underline disabled:cursor-not-allowed disabled:text-muted-foreground disabled:no-underline"
          >
            {isCoolingDown ? `Resend in ${countdown}s` : 'Resend code'}
          </button>
        )}
      </form>
    </AuthSplitLayout>
  );
}
